import React, { useState, useEffect, useRef } from 'react';
import { Mail, Plus, Trash2, Send, CheckCircle, ExternalLink } from 'lucide-react';
import { dashboardAPI, leadsAPI } from '../services/api';
import PageOptionsButton from './PageOptionsButton';
import { useAuth } from '../contexts/AuthContext';
import { isOwnedBy, resolveBdeIdentity } from '../utils/bdeOwnership';
import {
  handleRichPaste, buildTableHtml, insertHtmlAtCaret, sanitizePastedHtml, renderEmailBody,
  alignImage, joinPreviousLine, moveToOwnLine, moveImage, setImageWidth,
  makeRowFromImage, makeRowFromImageList, imagesInSelection, linkImage, imageLinkHref
} from './RichEmailEditor';
// Pasting a signature must always work, whatever it weighs — see utils/emailImages.
import { handleRichPasteWithImages, slimEmailBody } from '../utils/emailImages';
import { parseContactBook } from '../utils/contactBook';

const getDomainFromUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  let u = url.trim();
  if (!u.startsWith('http')) u = 'https://' + u;
  try {
    return new URL(u).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const getFirstName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.trim().split(/\s+/)[0] || '';
};

/**
 * A first name worked out from the address itself, for when we have no contact
 * name at all — better than greeting somebody as "Hello there".
 *
 * `ravi.kumar@x.com` / `ravi_kumar@` / `ravi-kumar@` -> Ravi, and `PragathiT@`
 * -> Pragathi: a capital in the middle of the local part is a real word
 * boundary somebody typed, so the letters after it are a surname initial, not
 * part of the first name.
 *
 * What it will NOT do is trim a trailing letter off a lower-case run, because
 * that turns "surabhianand" into "Surabhianan" as often as it turns "tarunr"
 * into "Tarun". `tarunr@theircompany.com` is fixed the other way round — the
 * server matches it against the names on that lead, and only then knows the
 * `r` is Raju. Whatever survives all of that is shown in the box beside the
 * recipient, marked as a guess, so it can be corrected BEFORE the send.
 */
const firstNameFromEmail = (email) => {
  const local = String(email || '').split('@')[0].split('+')[0];
  if (!local) return '';
  const parts = local.replace(/\d+/g, '').split(/[._\-]+/).filter(Boolean);
  let token = parts[0] || '';
  // "PragathiT" / "pragathiT" -> "Pragathi". Only a lower-to-upper step counts;
  // an all-caps local part (JASWANTHIB@) says nothing about where a name ends.
  const camel = /^(.*?[a-z])[A-Z]/.exec(token);
  if (camel && camel[1].length >= 3) token = camel[1];
  if (token.length < 2) return '';
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
};

/**
 * The name we actually KNOW for this recipient — scraped from the lead's POCs
 * or contact book, or typed into the box by hand. Empty when we have none.
 *
 * The scraped map is keyed in lower case, but an address typed by hand keeps
 * whatever case it was typed in — so `JASWANTHIB@AMGSOL.COM` missed its own
 * entry and every one of those emails went out as "Hello there". Match on the
 * normalised address.
 */
const knownFirstNameFor = (row, email) => {
  const key = String(email || '').trim().toLowerCase();
  const map = row.emailFirstNames || {};
  return String(map[key] || map[String(email || '').trim()] || '').trim();
};

/** The name to greet this recipient by — known if we have one, guessed if not. */
const firstNameFor = (row, email) =>
  knownFirstNameFor(row, email) || firstNameFromEmail(email) || '';

/**
 * Is that name a guess made from the address, rather than a name we hold?
 *
 * It matters at send time. A guess used to be sent to the server as if it were
 * a real name, which STOPPED the server looking the address up in the staff
 * list and in the lead's own contacts — so `PragathiT@amgsol.com` went out as
 * "Hello Pragathit" while her actual name sat one query away, and
 * `tarunr@libsys.com` as "Hello Tarunr" with "Tarun Raju" on the lead.
 */
const isGuessedFirstName = (row, email) => !knownFirstNameFor(row, email) && Boolean(firstNameFor(row, email));

/** A recipient as the send API wants it, with the guess flagged as a guess. */
const recipientFor = (row, email) => {
  const guessed = isGuessedFirstName(row, email);
  return { email, firstName: firstNameFor(row, email), firstNameGuessed: guessed };
};

const DEFAULT_TEMPLATE = 'Hi {{first_name}},\n\nFollowing up regarding {{company_name}}. We would like to schedule a call at your convenience.\n\nBest regards';
const DEFAULT_SUBJECT = 'Follow-up - {{company_name}}';

// Email style defaults (user can change font, size, line height, color per row)
const EMAIL_FONT_OPTIONS = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' }
];
const EMAIL_FONT_SIZE_OPTIONS = ['12px', '14px', '16px', '18px'];
const EMAIL_LINE_HEIGHT_OPTIONS = ['1.2', '1.4', '1.5', '1.6', '1.8'];
const DEFAULT_EMAIL_STYLE = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '14px',
  lineHeight: '1.5',
  textColor: '#333333'
};

// Nobody by name is CC'd by default any more (10 Sep 2026). Hima and Surabhi
// were on every email so that somebody senior always had a copy — the group
// inbox below now does that job, and it does it better: it is one address the
// whole team reads, and a prospect's reply-all reaches all of them rather than
// two people. Anyone can still be added to a row's CC by hand.
const DEFAULT_CC_EMAILS = [];

// The initial sales pitch, as a follow-up number — see the server's
// PITCH_FOLLOW_UP_NUMBER. Zero because the pitch comes before follow-up 1, and
// because no other row has ever used that number, so recording a sent pitch
// cannot overwrite a real follow-up.
const PITCH_FOLLOW_UP_NUMBER = 0;
// {{company_name}} however a template spells it — {{Company Name}},
// {{Company_Name}}… Only the exact form used to be filled, and 16 prospects got a
// subject that began "{{Company Name}} /" (11 Sep 2026). The server fills the same.
const COMPANY_PLACEHOLDER = /\{\{\s*company[\s_-]*name\s*\}\}/gi;
// Who has already had THIS row's email. The server lists it per email — the
// pitch, follow-up 1, 2, 3 — because having had the pitch is not having had
// Follow-up 1 (Energy2market: "7 of 9 sent" on a Follow-up 1 nobody had sent).
// An older server sent one list for the whole lead; that is used only then.
const alreadyEmailedFor = (fu, number) => {
  const byNumber = fu.already_emailed_by_number;
  const list = byNumber ? (byNumber[String(number)] || []) : (fu.already_emailed || []);
  return list.map(e => String(e).toLowerCase());
};

/**
 * The group inbox that keeps a copy of every email, and WHICH one.
 *
 * Both are CC now, not BCC: the recipient is meant to see that the team is on
 * the thread, and a reply-all then reaches the group rather than one BDA.
 *
 * Which inbox depends on what the email is. The pitch goes to Sales; the
 * messages that chase it go to the follow-up inbox, which exists for exactly
 * that. In this app the follow-ups are everything AFTER the pitch, so that is
 * the line drawn here — the pitch (follow-up number 0, stage "Initial Sales
 * Pitch") gets Sales, everything else gets the follow-up inbox.
 */
const SALES_GROUP_EMAIL = 'Sales@amgsol.com';
const FOLLOWUP_GROUP_EMAIL = 'AGSSalesFollowUp@amgsol.com';
const GROUP_EMAILS = [SALES_GROUP_EMAIL, FOLLOWUP_GROUP_EMAIL];

const groupCcFor = (stage, followUpNumber) => {
  // `== null` FIRST. `Number(null)` is 0, which is the pitch's own number, so a
  // row with no number — every row whose stage was just changed by hand — was
  // resolving to Sales however far into the follow-ups it had moved.
  const number = (followUpNumber == null || followUpNumber === '') ? null : Number(followUpNumber);
  if (number === PITCH_FOLLOW_UP_NUMBER) return SALES_GROUP_EMAIL;
  if (number != null && Number.isFinite(number) && number >= 1) return FOLLOWUP_GROUP_EMAIL;
  // No number to go on: the stage says what this email is. Anything that is not
  // the pitch is chasing one.
  const name = String(stage || '').trim();
  if (!name) return SALES_GROUP_EMAIL;
  return /initial sales pitch/i.test(name) ? SALES_GROUP_EMAIL : FOLLOWUP_GROUP_EMAIL;
};

const isGroupEmail = (address) =>
  GROUP_EMAILS.some(g => g.toLowerCase() === String(address || '').trim().toLowerCase());

/** The CC list for a row: the standing names, then this row's group inbox. */
const ccWithGroup = (stage, followUpNumber, existing = DEFAULT_CC_EMAILS) => {
  const group = groupCcFor(stage, followUpNumber);
  const kept = (existing || []).filter(e => !isGroupEmail(e));
  return [...kept, group];
};

// Stage options for emails
const STAGE_OPTIONS = [
  'Initial Sales Pitch',
  'Follow-up 1',
  'Follow-up 2',
  'Follow-up 3',
  'Deal Confirmation',
  'Meeting Scheduling Calls',
  'Onboarding Emails'
];

// Map follow-up number to default stage
const getDefaultStage = (followUpNumber, isPostSales) => {
  if (isPostSales) {
    // Post-sales follow-ups might be for onboarding or deal confirmation
    if (followUpNumber === 1) return 'Deal Confirmation';
    if (followUpNumber === 2) return 'Meeting Scheduling Calls';
    if (followUpNumber === 3) return 'Onboarding Emails';
    return 'Onboarding Emails';
  }
  // Pre-sales. Zero is the initial pitch — the follow-ups are the messages that
  // CHASE it, which is why they start at one.
  if (followUpNumber === PITCH_FOLLOW_UP_NUMBER) return 'Initial Sales Pitch';
  if (followUpNumber === 1) return 'Follow-up 1';
  if (followUpNumber === 2) return 'Follow-up 2';
  if (followUpNumber === 3) return 'Follow-up 3';
  return 'Initial Sales Pitch';
};

// Rows are shown in pitch order: Initial Sales Pitch, then Follow-up 1, 2, 3.
const STAGE_ORDER = STAGE_OPTIONS.reduce((acc, stage, i) => { acc[stage] = i; return acc; }, {});
// Ordered by the stage the row STARTED on, never the one currently selected.
// Sorting on the live value meant that picking a different template re-ordered
// the list under the cursor mid-edit: the card you were typing in jumped, and
// another company's card slid into its place — which is how an email ended up
// looking like it had been sent to the wrong client.
const stageRank = (row) => {
  const byStage = STAGE_ORDER[row.sortStage ?? row.stage];
  if (byStage !== undefined) return byStage;
  // Fall back to the follow-up number when the stage was renamed by hand
  return row.follow_up_number ? row.follow_up_number : 99;
};

// Get subject for stage (from API templates or default)
const getSubjectForStage = (stage, apiTemplates) => {
  if (apiTemplates?.[stage]?.subject) return apiTemplates[stage].subject;
  return DEFAULT_SUBJECT;
};

// Get template based on stage (apiTemplates from API overrides hardcoded)
const getTemplateForStage = (stage, companyName, apiTemplates) => {
  if (apiTemplates?.[stage]?.body) return apiTemplates[stage].body;
  const templates = {
    'Initial Sales Pitch': `Hello {{first_name}},

At American Green Solutions (AGS), we deliver AI-powered performance monitoring and APO services that act as a seamless extension of your existing operations team during off-hours. Our coverage includes 24/7/365 weekends, and holidays, when internal staffing is often limited or cost intensive.

By combining advanced AI analytics with experienced APO analysts, AGS ensures continuous asset oversight beyond standard business hours. While your team focuses on daytime operations, our team conducts in-depth performance analysis and proactive investigations overnight. This enables your staff to begin each business day with clear, actionable insights instead of unresolved issues, reducing operational delays and supporting faster, more confident decision-making.

How AGS APO Adds Value to Your Operations

• Off-Hours Advanced Analysis with AI Support
While your internal team manages daily activities, AGS leverages AI-driven tools overnight to identify trends, detect anomalies, and prioritize risks. Our analysts perform detailed root-cause analysis across alarms, performance deviations, inverter behaviour, string-level issues, and system anomalies.

• Reduced Lag and Faster Issue Resolution
Events identified during the day are fully analysed overnight using automated diagnostics and expert review. This allows corrective actions to start immediately the next morning, eliminating the typical 12–24-hour response delay.

• Optimized Truck Rolls
AI-assisted diagnostics help differentiate real faults from false alarms. This minimizes unnecessary site visits and ensures technicians are dispatched with accurate information and proper readiness, reducing operational costs and repeat visits.

• Cost Efficiency Without Additional Headcount
AGS APO provides continuous after-hours coverage without the need to expand internal staffing. This results in significant cost savings while maintaining high operational performance.

• 24/7/365 Portfolio Visibility
We deliver uninterrupted monitoring and APO support across nights, weekends, and holidays. AI-driven alerts and performance insights ensure no availability or performance gaps are missed.

Looking forward to discussing how AGS can support {{company_name}}.

Best regards`,
    'Follow-up 1': `Hi {{first_name}},\n\nFollowing up on my previous message regarding {{company_name}}. I wanted to check if you had a chance to review our proposal.\n\nLooking forward to hearing from you.\n\nBest regards`,
    'Follow-up 2': `Hi {{first_name}},\n\nThis is a friendly follow-up regarding {{company_name}}. I understand you may be busy, but I wanted to ensure my message didn't get lost.\n\nPlease let me know if you have any questions.\n\nBest regards`,
    'Follow-up 3': `Hi {{first_name}},\n\nFinal follow-up regarding {{company_name}}. We are here when you are ready to move forward.\n\nFeel free to reach out at your convenience.\n\nBest regards`,
    'Deal Confirmation': `Hi {{first_name}},\n\nThank you for choosing to work with us! I wanted to confirm the details of our agreement with {{company_name}}.\n\nPlease let me know if you have any questions.\n\nBest regards`,
    'Meeting Scheduling Calls': `Hi {{first_name}},\n\nI hope this message finds you well. I wanted to schedule a meeting to discuss the next steps for {{company_name}}.\n\nPlease let me know your availability.\n\nBest regards`,
    'Onboarding Emails': `Hi {{first_name}},\n\nWelcome aboard! We're excited to begin working with {{company_name}}.\n\nAttached you'll find the onboarding documents. Please review and let me know if you have any questions.\n\nBest regards`
  };
  return templates[stage] || DEFAULT_TEMPLATE;
};

// Escape HTML for safe insertion into email body
const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Read a File as base64 for attachment payload. Returns { filename, content } (content = base64 string only).
const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = reader.result;
    const base64 = (typeof dataUrl === 'string' && dataUrl.includes(',')) ? dataUrl.split(',')[1] : '';
    const filename = (file && (file.name || file.filename)) || 'attachment';
    resolve({ filename: String(filename), content: base64 || '' });
  };
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

// Plain text to HTML (for display in contenteditable)
const plainToHtml = (text) => {
  if (!text || typeof text !== 'string') return '<p><br></p>';
  return (text || '').split('\n').map(p => {
    const t = p.trim();
    return t ? `<p>${escapeHtml(t)}</p>` : '<p><br></p>';
  }).join('');
};

// Rich-text template editor: focus sets active editor so top toolbar applies here
const TemplateEditor = ({ rowIndex, row, updateRow, plainToHtml, DEFAULT_TEMPLATE, onFocusEditor, emailStyle, onSelectImage }) => {
  const ref = useRef(null);
  const template = row?.template;
  const renderedRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    // Same renderer the outgoing mail uses, so this IS the email.
    const html = renderEmailBody(template || DEFAULT_TEMPLATE, emailStyle);
    renderedRef.current = html;
    if (ref.current.innerHTML !== html) ref.current.innerHTML = html;
  }, [row?.id, template, DEFAULT_TEMPLATE, emailStyle]);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onFocus={() => onFocusEditor?.(ref.current, rowIndex)}
      onPaste={(e) => handleRichPasteWithImages(e, ref.current, (html) => updateRow(rowIndex, { template: html }))}
      /* Clicking a picture selects it, which is what puts the image controls
         (size, place in a row, and the link) on the toolbar. Clicking anywhere
         else lets it go again. */
      onClick={(e) => {
        onFocusEditor?.(ref.current, rowIndex);
        const img = e.target && e.target.tagName === 'IMG' ? e.target : null;
        onSelectImage?.(img, e.ctrlKey || e.metaKey);
      }}
      onBlur={(e) => {
        // Only save a real edit. Blurring an untouched row would otherwise
        // rewrite the stored template with its rendered form.
        const html = e.currentTarget.innerHTML;
        if (html !== renderedRef.current) updateRow(rowIndex, { template: html });
      }}
      className="email-body min-h-[120px] border border-t-0 border-gray-300 rounded-b-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:outline-none"
      /* No font imposed: an HTML body carries its own styling and must look
         here exactly as it will look in the recipient's inbox. */
      style={{}}
    />
  );
};

/**
 * Only build the editor for rows the person can actually see.
 *
 * Every row rendered its body into a contenteditable straight away. With 65
 * follow-ups on "All Pending" and a stage template of several megabytes — one
 * real one is 4 MB of inlined images — that is well over a hundred megabytes of
 * live DOM built in one go. The tab locks up, and clicks land on whatever row
 * has shuffled under the pointer by the time it responds.
 *
 * A row below the fold shows a placeholder of the same height instead, and
 * builds its editor the first time it scrolls into view. Once built it stays,
 * so scrolling back never loses anything: the text itself lives in `rows`, not
 * in the DOM.
 */
const DeferredEditor = ({ children }) => {
  const holderRef = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (shown || !holderRef.current) return undefined;
    // No IntersectionObserver (old browser, jsdom in the tests): just show it.
    if (typeof IntersectionObserver === 'undefined') { setShown(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) setShown(true);
    }, { rootMargin: '400px' });   // build it just before it is needed
    observer.observe(holderRef.current);
    return () => observer.disconnect();
  }, [shown]);
  if (shown) return children;
  return (
    <div
      ref={holderRef}
      className="min-h-[120px] border border-t-0 border-gray-300 rounded-b-lg px-3 py-2 bg-gray-50 text-sm text-gray-400 flex items-center"
    >
      Scroll here to edit this message…
    </div>
  );
};

// Build email HTML with inline styles (email clients need inline styles). templateText can be plain text or HTML (if it contains '<').
// When template is HTML we send it as-is (exact same template) wrapped only in the outer style div.
// The message that goes out is exactly what the editors show, because both come
// from renderEmailBody. See RichEmailEditor.
const buildEmailHtml = (templateText, emailStyle = {}) => renderEmailBody(templateText, {
  ...DEFAULT_EMAIL_STYLE,
  ...emailStyle
});

// Build rows from followUps: one row per (lead_id, follow_up_number) that has a date. apiTemplates optional from API.
const buildRowsFromFollowUps = (followUps, apiTemplates = {}) => {
  const rows = [];
  const seen = new Set();
  (followUps || []).forEach((fu) => {
    // Handle both lead_id (pre-sales direct from follow_ups table) and id (from sales_leads_view)
    const leadId = fu.lead_id || fu.id;
    const companyName = fu.company_name || 'Unknown';
    const isPostSales = !!fu.is_post_sales;
    const maxFu = isPostSales ? 4 : 3;
    // The pitch first: its own row, on the date the lead says the first sales
    // message went out. A lead can have this AND follow-ups — MML has both —
    // so it is never folded into follow-up 1.
    const pitchDate = !isPostSales ? fu.initial_pitch_date : null;
    const pitchKey = `${leadId || 'new'}-pitch`;
    if (pitchDate && !seen.has(pitchKey)) {
      seen.add(pitchKey);
      const pitchStage = getDefaultStage(PITCH_FOLLOW_UP_NUMBER, false);
      rows.push({
        id: pitchKey,
        lead_id: leadId,
        company_name: companyName,
        bde_name: fu.bde_name || '',
        is_post_sales: false,
        follow_up_number: PITCH_FOLLOW_UP_NUMBER,
        follow_up_date: pitchDate,
        stage: pitchStage,
        sortStage: pitchStage,
        templateKey: `stage:${pitchStage}`,
        emails: [],
        recipientAsTo: ccWithGroup(pitchStage, PITCH_FOLLOW_UP_NUMBER)
          .reduce((acc, e) => { acc[e.toLowerCase()] = true; return acc; }, {}),
        ccEmails: ccWithGroup(pitchStage, PITCH_FOLLOW_UP_NUMBER),
        includeCc: true,
        emailFirstNames: {},
        template: getTemplateForStage(pitchStage, null, apiTemplates),
        subject: getSubjectForStage(pitchStage, apiTemplates),
        emailStyle: { ...DEFAULT_EMAIL_STYLE },
        scraped: false,
        templateDirty: false,
        isInitialPitch: true,
        // Addresses that have already had the PITCH — so the composer can
        // untick them and say how many of the row are left.
        alreadyEmailed: alreadyEmailedFor(fu, PITCH_FOLLOW_UP_NUMBER),
        attachments: []
      });
    }
    for (let n = 1; n <= maxFu; n++) {
      const dateKey = n <= 3 ? `follow_up_date_${n}` : 'follow_up_date_4';
      const date = fu[dateKey];
      if (!date) continue;
      const key = `${leadId || 'new'}-${n}-${isPostSales ? 'ps' : 'pre'}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const statusKey = n <= 3 ? `follow_up_status${n === 1 ? '' : '_' + n}` : 'follow_up_status_4';
      const status = (fu[statusKey] || '').toString();
      if (status.toLowerCase() === 'completed') continue;
      const defaultStage = getDefaultStage(n, isPostSales);
      rows.push({
        id: key,
        lead_id: leadId,
        company_name: companyName,
        bde_name: fu.bde_name || '',
        is_post_sales: isPostSales,
        follow_up_number: n,
        follow_up_date: date,
        stage: defaultStage,
        sortStage: defaultStage,   // frozen: keeps this row's place in the list
        templateKey: `stage:${defaultStage}`,
        emails: [],
        recipientAsTo: ccWithGroup(defaultStage, n)
          .reduce((acc, e) => { acc[e.toLowerCase()] = true; return acc; }, {}),
        ccEmails: ccWithGroup(defaultStage, n),
        includeCc: true,
        emailFirstNames: {}, // email -> firstName for personalization
        template: getTemplateForStage(defaultStage, null, apiTemplates),
        subject: getSubjectForStage(defaultStage, apiTemplates),
        emailStyle: { ...DEFAULT_EMAIL_STYLE },
        scraped: false,
        templateDirty: false,   // set once the person edits the body or subject
        // Post-sales rows have their own numbering and are not in the pre-sales lists.
        alreadyEmailed: isPostSales ? [] : alreadyEmailedFor(fu, n),
        // Who has had the initial pitch. A contact who has NOT must not get a
        // follow-up first (15 Sep 2026: Energy2market, ecoligo… had contacts
        // added after the pitch, and Follow-up 1 was about to reach them).
        // Only known when the pitch went out through this app; empty otherwise.
        pitchedEmails: isPostSales ? [] : alreadyEmailedFor(fu, PITCH_FOLLOW_UP_NUMBER),
        attachments: []
      });
    }
  });
  return rows;
};

// Collect every address for a lead: POC 1-4 emails first, then EVERY contact_book
// entry, then domain-guess fallbacks for POCs that have a name but no address.
const collectLeadEmails = (lead) => {
  const domain = getDomainFromUrl(lead.company_url || '');
  const seen = new Set();
  const emails = [];
  const emailFirstNames = {};
  const add = (email, firstName) => {
    const e = (email && typeof email === 'string' ? email.trim() : '').toLowerCase();
    if (e && e.includes('@') && !seen.has(e)) {
      seen.add(e);
      emails.push(e);
      if (firstName) emailFirstNames[e] = getFirstName(firstName);
    } else if (e && firstName && !emailFirstNames[e]) {
      emailFirstNames[e] = getFirstName(firstName);
    }
  };
  const suggest = (name) => {
    const first = getFirstName(name);
    if (!first || !domain) return;
    const suggested = `${first.toLowerCase().replace(/[^a-z0-9]/g, '')}@${domain}`;
    if (!seen.has(suggested)) {
      seen.add(suggested);
      emails.push(suggested);
      emailFirstNames[suggested] = first;
    }
  };
  // Real POC addresses (poc_email is the view's own alias for POC 1)
  add(lead.email, lead.poc_name);
  add(lead.poc_email, lead.poc_name);
  add(lead.poc2_email, lead.poc2_name);
  add(lead.poc3_email, lead.poc3_name);
  add(lead.poc4_email, lead.poc4_name);
  // Every contact book entry
  parseContactBook(lead.contact_book || '').forEach(({ name, email }) => add(email, name));
  // Guessed addresses last, and only for POCs we have no real address for
  suggest(lead.poc_name);
  suggest(lead.poc2_name);
  suggest(lead.poc3_name);
  suggest(lead.poc4_name);
  return { emails, emailFirstNames };
};

// Merge freshly scraped addresses into a row without dropping anything the user
// typed or unticked. Scraped addresses lead; hand-added ones keep their place after.
const mergeScrapedIntoRow = (row, lead) => {
  const { emails: scraped, emailFirstNames } = collectLeadEmails(lead);
  const existing = (row.emails || []).map(e => (e || '').trim().toLowerCase()).filter(Boolean);
  const merged = [...scraped, ...existing.filter(e => !scraped.includes(e))];
  const recipientAsTo = { ...(row.recipientAsTo || {}) };
  // Someone who has already had this email starts UNTICKED. Scraping the
  // contact book pulls in every address every time the list reloads, and
  // ticking them all is how a person gets the same pitch twice.
  const already = new Set(row.alreadyEmailed || []);
  const pitched = new Set(row.pitchedEmails || []);
  // On a follow-up, someone the pitch never reached starts UNTICKED — they would
  // otherwise get "following up on my previous email" about an email they never
  // had. Applies only when this lead's pitch is on record (pitched is not empty).
  const neverPitched = (e) => !row.isInitialPitch && pitched.size > 0 && !pitched.has(e) && !already.has(e);
  merged.forEach(e => { if (recipientAsTo[e] === undefined) recipientAsTo[e] = !already.has(e) && !neverPitched(e); });
  return {
    // NEVER overwrite a name the person typed. The scrape re-runs every time
    // the follow-up list reloads, so a custom company name was being replaced
    // with the lead's own name behind the user's back — and then went out in
    // the email, because {{company_name}} is filled from this field.
    company_name: row.companyDirty
      ? row.company_name
      : (lead.company_name || row.company_name),
    emails: merged,
    recipientAsTo,
    notPitched: merged.filter(e => e.includes('@') && neverPitched(e)),
    emailFirstNames: { ...(row.emailFirstNames || {}), ...emailFirstNames },
    scraped: true
  };
};

// Helper to get date ranges for filtering
const getDateRanges = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  // Get start of this week (Monday)
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
  startOfWeek.setDate(today.getDate() - diff);
  
  // Get end of this week (Sunday)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  
  return { today, tomorrow, startOfWeek, endOfWeek };
};

// Parse YYYY-MM-DD as local date (avoid UTC shift)
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Format local date as YYYY-MM-DD
const formatDateLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const SendEmails = ({
  followUps,
  loading,
  onRefresh,
  onMarkCompleted,
  onViewLead,
  onExportExcel,
  onExportPDF,
  showExport = false
}) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [markingDoneId, setMarkingDoneId] = useState(null);
  const [draftingId, setDraftingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('week'); // 'today', 'week', 'all'
  const [bdeRoster, setBdeRoster] = useState(null); // decides who the signed-in user is (see bdeOwnership)
  const [emailSenders, setEmailSenders] = useState([]); // [{ slot, email }, ...] from backend
  const [senderSlot, setSenderSlot] = useState(null); // 1-4 or null; default from user.email_sender_slot
  // The address this user actually sends from, as computed by the backend
  // (their own Microsoft 365 mailbox when they have one).
  const [effectiveSender, setEffectiveSender] = useState(null);
  const [globalEmailStyle, setGlobalEmailStyle] = useState({ ...DEFAULT_EMAIL_STYLE }); // One style for every email (no per-row)
  const [apiTemplates, setApiTemplates] = useState({}); // stage -> { subject, body } from API (Templates page)
  // Rows where somebody asked to see the addresses that already had the email.
  const [expandedRecipients, setExpandedRecipients] = useState({});
  const activeEditorRef = useRef(null);
  const activeRowIndexRef = useRef(null);
  const fileInputRef = useRef(null); // attachments
  const inlineImageInputRef = useRef(null); // images dropped into the body
  const savedSelectionRef = useRef(null); // for Link: save selection before prompt, restore after
  // ---- Arranging and linking images in the composer ------------------------
  // The Templates page has had these controls for a while; the composer had
  // none, so a signature could be built there and then not touched here — no
  // resizing, no side-by-side row, and no way to point the LinkedIn and
  // Instagram badges at anything.
  const [selectedImg, setSelectedImg] = useState(null);
  const [selectedImgWidth, setSelectedImgWidth] = useState('');
  const [selectedImgHref, setSelectedImgHref] = useState('');
  const [pickedImgs, setPickedImgs] = useState([]); // Ctrl/Cmd-click builds a set

  const widthOf = (img) => String(
    parseInt(img.getAttribute('width'), 10) || parseInt(img.style.width, 10)
    || img.clientWidth || img.naturalWidth || ''
  );

  // Outlines are cleared from the DOM, not from remembered state: this runs
  // from an event handler, where a captured array is always a render behind.
  const clearImageOutlines = (keep) => {
    const el = activeEditorRef.current;
    if (!el) return;
    el.querySelectorAll('img').forEach(i => { if (i !== keep) i.style.outline = ''; });
  };

  const selectComposerImage = (img, additive = false) => {
    if (additive && img) {
      img.style.outline = '2px solid #679632';
      setPickedImgs(prev => (prev.includes(img) ? prev : [...prev, img]));
      setSelectedImg(img);
      setSelectedImgWidth(widthOf(img));
      setSelectedImgHref(imageLinkHref(img));
      return;
    }
    clearImageOutlines(img);
    setPickedImgs([]);
    if (!img) { setSelectedImg(null); setSelectedImgWidth(''); setSelectedImgHref(''); return; }
    img.style.outline = '2px solid #679632';
    setSelectedImg(img);
    setSelectedImgWidth(widthOf(img));
    setSelectedImgHref(imageLinkHref(img));
  };

  // Write the edited body back to the row it belongs to.
  //
  // Saving re-renders the editor from the stored body, which REPLACES every
  // node in it — so the image we were holding becomes a detached element and
  // every further click on the toolbar silently does nothing to the visible
  // body. That is what made "Make a row" feel broken: the first press worked,
  // and nothing after it did. Re-find the same image by its position once the
  // new DOM is in place, and hand the toolbar a live node again.
  /**
   * Toolbar edits that are not about images: bullets and text colour.
   *
   * Two things make this fiddly, and both are handled here rather than in each
   * button. The SELECTION is lost the moment focus leaves the body — a colour
   * picker is a real focus change, unlike a button press we can cancel — so the
   * range is remembered on the way in and put back before the command runs. And
   * the row's saved body has to be updated afterwards: without that the change
   * is on screen but not in `rows`, so the next re-render throws it away.
   */
  const rememberSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (activeEditorRef.current && activeEditorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const commitActiveEditor = () => {
    const el = activeEditorRef.current;
    const idx = activeRowIndexRef.current;
    if (!el || idx == null || idx < 0) return;
    updateRow(idx, { template: el.innerHTML });
  };

  const runInActiveEditor = (command, value = null, { restore = false } = {}) => {
    const el = activeEditorRef.current;
    if (!el) return;
    el.focus();
    if (restore && savedSelectionRef.current) {
      try {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      } catch (_) { /* the body was re-rendered under us; carry on with the caret where it is */ }
    }
    // CSS, not <font>: a colour written as `style="color: …"` is what the rest
    // of the pipeline (and every mail client) reads, and <font> is not.
    try { document.execCommand('styleWithCSS', false, true); } catch (_) {}
    document.execCommand(command, false, value);
    try { document.execCommand('styleWithCSS', false, false); } catch (_) {}
    savedSelectionRef.current = null;
    commitActiveEditor();
  };

  const commitImageChange = (keepImage = true) => {
    const el = activeEditorRef.current;
    const idx = activeRowIndexRef.current;
    if (!el || idx == null || idx < 0) return;
    const images = Array.from(el.querySelectorAll('img'));
    const position = keepImage && selectedImg ? images.indexOf(selectedImg) : -1;
    updateRow(idx, { template: el.innerHTML });
    if (position < 0) { selectComposerImage(null); return; }
    // After React has swapped the nodes.
    setTimeout(() => {
      const live = activeEditorRef.current;
      if (!live) return;
      const again = live.querySelectorAll('img')[position];
      selectComposerImage(again || null);
    }, 0);
  };

  const arrangeSelected = (action) => {
    const img = selectedImg;
    const root = activeEditorRef.current;
    if (!img || !root) return;
    if (action === 'left' || action === 'center' || action === 'right') alignImage(img, action, root);
    if (action === 'join') joinPreviousLine(img, root);
    if (action === 'own') moveToOwnLine(img, root);
    if (action === 'moveLeft' || action === 'moveRight') moveImage(img, action === 'moveLeft' ? 'left' : 'right', root);
    if (action === 'row') {
      const chosen = pickedImgs.length >= 2 ? pickedImgs : imagesInSelection(root);
      const count = chosen.length >= 2 ? makeRowFromImageList(chosen, root) : makeRowFromImage(img, root);
      chosen.forEach(i => { i.style.outline = ''; });
      setPickedImgs([]);
      if (count < 2) {
        window.alert('Pick the images first: drag across them, or Ctrl/Cmd-click each one — then press Make a row. Clicking a single image lines it up with the image-only lines below it.');
      }
    }
    commitImageChange();
  };


  const resizeSelected = (width) => {
    if (!selectedImg) return;
    const applied = setImageWidth(selectedImg, width);
    if (applied) setSelectedImgWidth(String(applied));
    commitImageChange();
  };

  // Point a picture at a URL — this is how the LinkedIn and Instagram badges in
  // a signature become clickable. execCommand('createLink') cannot do it: it
  // needs selected TEXT, and an image is not text.
  const linkSelectedImage = () => {
    if (!selectedImg) return;
    const current = imageLinkHref(selectedImg);
    const url = window.prompt('Link this image to (leave empty to remove the link):', current || 'https://');
    if (url === null) return;                       // Cancel
    const cleaned = url.trim() === 'https://' ? '' : url.trim();
    linkImage(selectedImg, cleaned);
    setSelectedImgHref(imageLinkHref(selectedImg));
    commitImageChange();
  };

  // Non-admins only ever see their OWN follow-ups, and only Today / This Week.
  // Admins keep the full list plus the "All" tab.
  const identity = resolveBdeIdentity(user, bdeRoster);
  const restrictToOwn = identity.restrictToOwn;
  const effectiveFilter = restrictToOwn && filter === 'all' ? 'week' : filter;

  // A row with no BDE on it was added by hand on this screen — it belongs to
  // whoever added it, so it must never be filtered away from them.
  const visibleRows = restrictToOwn
    ? rows.filter(row => !row.bde_name || isOwnedBy(row.bde_name, identity))
    : rows;

  // Filter rows based on selected filter
  const filteredRows = visibleRows.filter(row => {
    if (effectiveFilter === 'all') return true;
    
    const { today, tomorrow, startOfWeek, endOfWeek } = getDateRanges();
    const rowDate = parseDate(row.follow_up_date);
    
    if (!rowDate) return effectiveFilter === 'all'; // No date = show in 'all' only
    
    if (effectiveFilter === 'today') {
      // Only follow-ups due today (same date)
      return rowDate.getTime() === today.getTime();
    }
    
    if (effectiveFilter === 'week') {
      // This week (including overdue/past dates)
      return rowDate <= endOfWeek;
    }
    
    return true;
  }).sort((a, b) => {
    const stageDiff = stageRank(a) - stageRank(b);
    if (stageDiff !== 0) return stageDiff;
    const da = parseDate(a.follow_up_date);
    const db = parseDate(b.follow_up_date);
    if (da && db && da.getTime() !== db.getTime()) return da - db;
    return (a.company_name || '').localeCompare(b.company_name || '');
  });

  // Count for filter badges
  const getCounts = () => {
    const { today, tomorrow, startOfWeek, endOfWeek } = getDateRanges();
    let todayCount = 0;
    let weekCount = 0;
    
    visibleRows.forEach(row => {
      const rowDate = parseDate(row.follow_up_date);
      if (!rowDate) return;
      
      if (rowDate.getTime() === today.getTime()) todayCount++;
      if (rowDate <= endOfWeek) weekCount++;
    });
    
    return { todayCount, weekCount, allCount: visibleRows.length };
  };
  
  const counts = getCounts();

  // Roster of BDEs — used to resolve the signed-in user's real name/role
  useEffect(() => {
    let cancelled = false;
    dashboardAPI.getBDEUsers()
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        if (!cancelled) setBdeRoster(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fetch email templates from API (used as defaults for subject/body per stage)
  useEffect(() => {
    let cancelled = false;
    dashboardAPI.getEmailTemplates().then(res => {
      if (!cancelled && res?.success && res.templates) setApiTemplates(res.templates);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fetch custom named templates (shown in template dropdown when sending)
  const [customTemplates, setCustomTemplates] = useState([]);
  useEffect(() => {
    // No `user.id` guard: the API identifies the caller from the session token,
    // and an SSO session's id is not a bde_users id anyway.
    if (!user) return undefined;
    let cancelled = false;
    dashboardAPI.getCustomEmailTemplates(user.id, 'all', { meta: true }).then(res => {
      if (!cancelled && res?.success && Array.isArray(res.data)) setCustomTemplates(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // Sync rows with followUps whenever follow-ups data updates (e.g. after Refresh)
  // Preserves user edits. Never removes a row just because it's not in the latest API response
  // (e.g. after Send, row stays until user clicks Done)
  useEffect(() => {
    const safeFollowUps = Array.isArray(followUps) ? followUps : [];
    const built = buildRowsFromFollowUps(safeFollowUps, apiTemplates);
    const builtIds = new Set(built.map(r => r.id));
    
    setRows(prev => {
      const existingById = {};
      prev.forEach(r => { existingById[r.id] = r; });
      // Merge: rows from API get latest data, keep your edits for matching rows
      const merged = built.map(b => {
        const existing = existingById[b.id];
        if (!existing) return b;
        const row = { ...b, ...existing }; // keep emails, ccEmails, attachments, edits
        // The stage templates arrive AFTER the first render, so the first build
        // falls back to the built-in wording. Letting `existing` win outright
        // meant that fallback stuck forever — the composer showed plain default
        // text while the Templates page showed the real template. Take the
        // freshly built body unless this row has actually been edited.
        if (!existing.templateDirty) {
          row.template = b.template;
          row.subject = b.subject;
        }
        return row;
      });
      // A row the server has stopped sending is FINISHED — everyone on it has
      // had the email — so it goes. This used to keep every such row on screen
      // "until the user clicks Done", which is why EG.D sat there after all 21
      // of its contacts had been written to: the server had dropped it and the
      // page put it straight back.
      //
      // Two exceptions, both about not destroying someone's work: a row added
      // by hand on this screen has no follow-up behind it and belongs to
      // whoever typed it, and a row in the middle of being sent must not
      // disappear underneath the send.
      const keptFromPrev = prev.filter(r => r.id && !builtIds.has(r.id)
        && (!r.lead_id || r.id === sendingId));
      return [...merged, ...keptFromPrev];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followUps, apiTemplates, sendingId]);

  // Fetch configured email sender accounts; default to logged-in user's email_sender_slot if set
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardAPI.getEmailSenders({ userId: user?.id, email: user?.email });
        const list = (res?.data || res || []);
        if (!cancelled && res?.effective) setEffectiveSender(res.effective);
        if (!cancelled && Array.isArray(list)) {
          setEmailSenders(list);
          const userSlot = user?.email_sender_slot;
          if (userSlot >= 1 && userSlot <= 4 && list.some(s => s.slot === userSlot)) {
            setSenderSlot(userSlot);
          } else if (list.length > 1 && list[0]?.slot != null) {
            setSenderSlot(list[0].slot);
          }
        }
      } catch (_) {
        // ignore; single account or no senders configured
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email_sender_slot, user?.id, user?.email]);

  // Auto-scrape addresses for the rows ON SCREEN: POC 1-4 emails + the whole
  // contact book. Only the visible rows are fetched (the list can hold hundreds
  // of follow-ups — walking all of them one at a time meant the rows you were
  // actually looking at got their addresses last, or never), a handful of leads
  // at a time, and it re-runs whenever the follow-up list reloads.
  const autoScrapeAttemptedRef = useRef(new Set());
  useEffect(() => {
    // New follow-up data => let every row be scraped again
    autoScrapeAttemptedRef.current = new Set();
  }, [followUps]);

  const visibleScrapeKey = filteredRows.map(r => r.id).join('|');
  useEffect(() => {
    if (scraping) return;
    const targets = filteredRows.filter(r => r.lead_id && !autoScrapeAttemptedRef.current.has(r.id));
    if (targets.length === 0) return;

    targets.forEach(r => autoScrapeAttemptedRef.current.add(r.id));
    setScraping(true);

    (async () => {
      // One fetch per lead, six leads at a time
      const leadIds = [...new Set(targets.map(r => r.lead_id))];
      const leadsById = {};
      const CONCURRENCY = 6;
      for (let i = 0; i < leadIds.length; i += CONCURRENCY) {
        await Promise.all(leadIds.slice(i, i + CONCURRENCY).map(async (leadId) => {
          try {
            const res = await leadsAPI.getById(leadId);
            const lead = res?.data ?? res;
            if (lead) leadsById[leadId] = lead;
          } catch (err) {
            // skip this lead
          }
        }));
      }
      setRows(prev => prev.map(r => (
        leadsById[r.lead_id] ? { ...r, ...mergeScrapedIntoRow(r, leadsById[r.lead_id]) } : r
      )));
      setScraping(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScrapeKey, scraping]);

  // ---- Which rows continue a conversation ---------------------------------
  //
  // A follow-up to someone we have already written to about this lead is sent as
  // a REPLY, under the subject that conversation started with — that is what
  // keeps the pitch and follow-ups 1/2/3 in one thread instead of three. The
  // sender has to see that before they send, so the row says so and shows the
  // exact subject. Looked up for the rows on screen, in one call.
  const [threads, setThreads] = useState({});   // "<leadId>|<email>" -> { subject, messageCount }
  const threadKey = (leadId, email) => `${leadId}|${String(email || '').trim().toLowerCase()}`;
  const visibleThreadKey = [...new Set(filteredRows.map(r => r.lead_id).filter(Boolean))].sort().join('|');
  useEffect(() => {
    const leadIds = visibleThreadKey ? visibleThreadKey.split('|') : [];
    if (leadIds.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardAPI.getEmailThreads(leadIds);
        if (!cancelled && res && res.threads) setThreads(prev => ({ ...prev, ...res.threads }));
      } catch (err) {
        // No badge, no harm: the send itself decides the threading either way.
      }
    })();
    return () => { cancelled = true; };
  }, [visibleThreadKey]);

  // The thread this row's TO recipients are already in, if any.
  const threadForRow = (row) => {
    if (!row || !row.lead_id) return null;
    const to = (row.emails || []).filter(e => (row.recipientAsTo || {})[(e || '').trim().toLowerCase()] === true);
    const found = to.map(e => threads[threadKey(row.lead_id, e)]).filter(Boolean);
    if (found.length === 0) return null;
    return { ...found[0], threadedCount: found.length, recipientCount: to.length };
  };

  const scrapeRow = async (rowIndex) => {
    const row = rowIndex >= 0 ? rows[rowIndex] : null;
    if (!row || !row.lead_id) {
      setMessage({ type: 'error', text: 'No lead linked to this row.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    setScraping(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await leadsAPI.getById(row.lead_id);
      const lead = res?.data ?? res;
      if (!lead) {
        setMessage({ type: 'error', text: 'Lead not found.' });
        setScraping(false);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        return;
      }
      let count = 0;
      setRows(prev => {
        const next = [...prev];
        const merged = mergeScrapedIntoRow(next[rowIndex], lead);
        count = merged.emails.length;
        next[rowIndex] = { ...next[rowIndex], ...merged };
        return next;
      });
      setMessage({ type: 'success', text: `Scraped ${count} email(s) from POCs and contact book. Untick any you want in CC instead of To.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to load lead' });
    } finally {
      setScraping(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Every mutator below is handed a position in `rows`. findIndex returns -1 for
  // a row that has just gone (sent, or marked Done), and `next[-1] = {...}`
  // writes to a property called "-1" — the edit vanishes with no error at all.
  // Address a row by its id. Positions shift under async work; ids do not.
  const updateRowById = (rowId, updates) => {
    if (!rowId) return;
    const touchesContent = updates.template !== undefined || updates.subject !== undefined;
    setRows(prev => prev.map(r => (
      r.id === rowId
        ? {
          ...r,
          ...updates,
          ...(touchesContent ? { templateDirty: true } : {}),
          ...(updates.company_name !== undefined ? { companyDirty: true } : {})
        }
        : r
    )));
  };

  const updateRow = (rowIndex, updates) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const touchesContent = updates.template !== undefined || updates.subject !== undefined;
      next[rowIndex] = {
        ...next[rowIndex],
        ...updates,
        ...(touchesContent ? { templateDirty: true } : {}),
        // Typing a company name pins it: the auto-scrape must not put the
        // lead's own name back over the top of it.
        ...(updates.company_name !== undefined ? { companyDirty: true } : {})
      };
      return next;
    });
  };

  // Apply a template (stage or custom) to a row: sets subject + body from template
  // Bodies are pulled only when a template is actually picked, and cached on
  // the list so picking it again is instant.
  //
  // Keyed by ROW ID, not by position: the fetch takes a moment and the list can
  // re-render underneath it, at which point an index points at somebody else's
  // row and the template lands on the wrong company.
  const loadCustomBody = async (id, rowId, templateKey) => {
    try {
      const res = await dashboardAPI.getCustomEmailTemplate(id);
      const tpl = res?.data || res?.template;
      if (!res?.success || !tpl) throw new Error('That template came back empty.');
      setCustomTemplates(prev => prev.map(c => (c.id === id ? { ...c, ...tpl } : c)));
      setRows(prev => prev.map(r => (
        r.id === rowId && r.templateKey === templateKey
          ? { ...r, template: tpl.body || '', subject: tpl.subject || r.subject, templateLoading: false }
          : r
      )));
    } catch (e) {
      setRows(prev => prev.map(r => (r.id === rowId ? { ...r, templateLoading: false } : r)));
      setMessage({ type: 'error', text: 'Could not load that template: ' + (e.response?.data?.error || e.message) });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    }
  };

  // The row's ONE template control. A stage entry also sets the row's stage, so
  // the follow-up is still recorded against the right step of the pitch.
  const applyTemplateToRow = (rowIndex, templateKey) => {
    if (!templateKey || rowIndex < 0) return;
    const row = rows[rowIndex];
    if (!row) return;

    // Everything is decided HERE, not inside the setRows updater. React runs an
    // updater later, during render — so a variable assigned inside one is still
    // unset on the line after setRows(), and the fetch that was supposed to
    // follow never happened. That is what left the row saying "loading…" for
    // ever with the previous body still on screen.
    if (templateKey.startsWith('stage:')) {
      const stage = templateKey.replace(/^stage:/, '');
      // Changing what this email IS changes which group inbox keeps the copy —
      // a pitch goes to Sales, anything chasing it to the follow-up inbox. Only
      // the group address is swapped; names added by hand stay exactly as they
      // are, and so does an unticked box.
      const nextCc = ccWithGroup(stage, null, row.ccEmails || DEFAULT_CC_EMAILS);
      const recipientAsTo = { ...(row.recipientAsTo || {}) };
      const nextGroup = groupCcFor(stage, null).toLowerCase();
      GROUP_EMAILS.forEach(g => {
        const key = g.toLowerCase();
        if (key !== nextGroup) delete recipientAsTo[key];
      });
      if (recipientAsTo[nextGroup] === undefined) recipientAsTo[nextGroup] = true;
      updateRowById(row.id, {
        stage,
        templateKey,
        templateLoading: false,
        ccEmails: nextCc,
        recipientAsTo,
        subject: getSubjectForStage(stage, apiTemplates),
        template: getTemplateForStage(stage, null, apiTemplates)
      });
      return;
    }

    if (!templateKey.startsWith('custom:')) return;
    const id = parseInt(templateKey.replace(/^custom:/, ''), 10);
    const custom = (customTemplates || []).find(c => c.id === id);
    if (!custom) return;

    // The dropdown is fetched WITHOUT bodies (one real template is 4 MB), so a
    // freshly picked template has only its name and subject in hand. Nothing
    // used to go and fetch the body, which is why picking one of your own
    // templates changed the subject and left the previous body sitting there.
    const haveBody = custom.body != null && custom.body !== '';
    updateRowById(row.id, {
      templateKey,
      templateLoading: !haveBody,
      subject: custom.subject || row.subject || '',
      template: haveBody ? custom.body : (row.template || '')
    });
    if (!haveBody) loadCustomBody(id, row.id, templateKey);
  };

  const updateEmail = (rowIndex, emailIndex, value, firstName) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      const emails = [...(row.emails || [])];
      const emailFirstNames = { ...(row.emailFirstNames || {}) };
      const recipientAsTo = { ...(row.recipientAsTo || {}) };
      const v = (value && typeof value === 'string' ? value.trim() : '').toLowerCase();
      if (emailIndex >= emails.length) {
        emails.push(value);
        if (firstName) emailFirstNames[v] = firstName;
        if (value) recipientAsTo[v] = true;
      } else {
        const old = emails[emailIndex];
        emails[emailIndex] = value;
        const o = (old && typeof old === 'string' ? old.trim() : '').toLowerCase();
        if (o && emailFirstNames[o]) delete emailFirstNames[o];
        if (firstName) emailFirstNames[v] = firstName;
        if (old && recipientAsTo[o] !== undefined) {
          recipientAsTo[v] = recipientAsTo[o];
          delete recipientAsTo[o];
        } else if (value) recipientAsTo[v] = true;
      }
      row.emails = emails.filter(Boolean);
      row.emailFirstNames = emailFirstNames;
      row.recipientAsTo = recipientAsTo;
      next[rowIndex] = row;
      return next;
    });
  };

  // Toggle whether a recipient is in To (true) or CC (false). No delete - uncheck to send as CC.
  const toggleRecipientToCc = (rowIndex, emailIndex) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      const email = (row.emails || [])[emailIndex];
      if (!email) return next;
      const recipientAsTo = { ...(row.recipientAsTo || {}) };
      const key = (email || '').trim().toLowerCase();
      recipientAsTo[key] = !recipientAsTo[key];
      row.recipientAsTo = recipientAsTo;
      next[rowIndex] = row;
      return next;
    });
  };

  // Toggle To/CC for any email by address (used for both scraped emails and CC list).
  const toggleRecipientAsToByEmail = (rowIndex, email) => {
    const e = (email && typeof email === 'string' ? email.trim() : '').toLowerCase();
    if (!e || !e.includes('@') || rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      const recipientAsTo = { ...(row.recipientAsTo || {}) };
      recipientAsTo[e] = !recipientAsTo[e];
      row.recipientAsTo = recipientAsTo;
      next[rowIndex] = row;
      return next;
    });
  };

  const addEmailToRow = (rowIndex) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row.emails = [...(row.emails || []), ''];
      row.recipientAsTo = row.recipientAsTo || {};
      next[rowIndex] = row;
      return next;
    });
  };

  const updateCcEmail = (rowIndex, ccIndex, value) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      const recipientAsTo = { ...(row.recipientAsTo || {}) };
      const ccEmails = [...(row.ccEmails || [])];
      if (ccIndex >= ccEmails.length) {
        ccEmails.push(value);
      } else {
        ccEmails[ccIndex] = value;
      }
      row.ccEmails = ccEmails.filter(Boolean);
      const newVal = (value && typeof value === 'string' ? value.trim() : '').toLowerCase();
      if (newVal && newVal.includes('@') && recipientAsTo[newVal] === undefined) recipientAsTo[newVal] = false;
      row.recipientAsTo = recipientAsTo;
      next[rowIndex] = row;
      return next;
    });
  };

  const removeCcEmail = (rowIndex, ccIndex) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row.ccEmails = (row.ccEmails || []).filter((_, i) => i !== ccIndex);
      next[rowIndex] = row;
      return next;
    });
  };

  const addCcEmailToRow = (rowIndex) => {
    if (rowIndex < 0) return;
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[rowIndex] };
      row.ccEmails = [...(row.ccEmails || []), ''];
      next[rowIndex] = row;
      return next;
    });
  };

  const addNewRow = () => {
    // Set date so the new row appears in the current filter (use local date).
    // effectiveFilter, not filter: a BDA is always on Today/This Week, so the row
    // needs today's date or it would be created straight out of view.
    const { today } = getDateRanges();
    const newDate = effectiveFilter === 'today' || effectiveFilter === 'week' ? formatDateLocal(today) : '';
    setRows(prev => [...prev, {
      id: `new-${Date.now()}`,
      lead_id: null,
      company_name: '',
      bde_name: identity.name || user?.name || '',
      is_post_sales: false,
      follow_up_number: 1,
      follow_up_date: newDate,
      stage: 'Initial Sales Pitch',
      sortStage: 'Initial Sales Pitch',
      templateKey: 'stage:Initial Sales Pitch',
      emails: [''],
      recipientAsTo: ccWithGroup('Initial Sales Pitch', null)
        .reduce((acc, e) => { acc[e.toLowerCase()] = true; return acc; }, {}),
      ccEmails: ccWithGroup('Initial Sales Pitch', null),
      includeCc: true,
      emailFirstNames: {},
      template: getTemplateForStage('Initial Sales Pitch', null, apiTemplates),
      subject: getSubjectForStage('Initial Sales Pitch', apiTemplates),
      emailStyle: { ...DEFAULT_EMAIL_STYLE },
      scraped: false,
      attachments: []
    }]);
  };

  const deleteRow = async (rowIndex) => {
    if (rowIndex < 0) return;
    const row = rows[rowIndex];
    // Off the screen straight away — nobody should watch a spinner to delete
    // something — and off the DATABASE too, or the next refresh rebuilds the
    // row and it comes back, which is what made this button feel broken.
    setRows(prev => prev.filter((_, i) => i !== rowIndex));
    if (row && row.lead_id && row.follow_up_number != null && !row.is_post_sales) {
      try {
        await leadsAPI.deleteFollowUp(row.lead_id, row.follow_up_number,
          row.follow_up_date ? { followUpDate: row.follow_up_date } : {});
        if (onRefresh) onRefresh();
        setMessage({ type: 'success', text: `${row.company_name || 'That row'} deleted — it will not come back.` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (err) {
        // Put it back rather than pretend: a row that is still in the database
        // WILL reappear, and it is better to see that now.
        setRows(prev => [row, ...prev]);
        setMessage({ type: 'error', text: `Could not delete ${row.company_name || 'that row'}: ${err.response?.data?.error || err.message}` });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    }
  };

  // Scrape all filtered rows that have a lead_id (refresh POC + contact_book from lead)
  const scrapeAll = async () => {
    const toScrape = filteredRows.filter(row => row.lead_id);
    
    if (toScrape.length === 0) {
      setMessage({ type: 'info', text: 'No rows with a linked lead to scrape.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    
    setScraping(true);
    setMessage({ type: '', text: '' });
    let successCount = 0;
    
    // One fetch per lead, even when a lead has several follow-up rows on screen
    const leadIds = [...new Set(toScrape.map(r => r.lead_id))];
    const leadsById = {};
    for (const leadId of leadIds) {
      try {
        const res = await leadsAPI.getById(leadId);
        const lead = res?.data ?? res;
        if (lead) leadsById[leadId] = lead;
      } catch (err) {
        // Continue with next lead
      }
    }
    
    const scrapedIds = new Set(toScrape.filter(r => leadsById[r.lead_id]).map(r => r.id));
    successCount = scrapedIds.size;
    setRows(prev => prev.map(r => (
      scrapedIds.has(r.id) ? { ...r, ...mergeScrapedIntoRow(r, leadsById[r.lead_id]) } : r
    )));
    
    setScraping(false);
    setMessage({ type: 'success', text: `Scraped POC + contact book emails for ${successCount} row(s).` });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  /**
   * Finish a row only when the row is actually finished.
   *
   * A send goes to the TICKED recipients. The Mobility House has twenty
   * contacts; the pitch went to one of them, the row was marked done and the
   * lead disappeared from the Emails page and the schedule with nineteen people
   * unpitched. Sending to some of a row's addresses is progress, not
   * completion.
   *
   * So: anything still listed on the row and not yet sent keeps the row alive.
   * The addresses that DID go are unticked, so pressing Send again reaches the
   * rest and cannot mail the same person twice. Done still closes it by hand,
   * and a row where everyone has been sent to closes itself as before.
   */
  const settleRowAfterSend = async (row, sentTo, describe) => {
    const sent = new Set((sentTo || []).map(e => String(e || '').trim().toLowerCase()).filter(Boolean));
    const remaining = (row.emails || [])
      .map(e => String(e || '').trim().toLowerCase())
      .filter(e => e && e.includes('@') && !sent.has(e));
    if (remaining.length === 0) {
      await completeAndRemoveRow(row);
      return { closed: true, remaining: 0 };
    }
    setRows(prev => prev.map(r => (r.id === row.id
      ? {
        ...r,
        recipientAsTo: {
          ...(r.recipientAsTo || {}),
          ...Object.fromEntries([...sent].map(e => [e, false])),
          ...Object.fromEntries(remaining.map(e => [e, true]))
        }
      }
      : r)));
    if (describe) {
      setMessage({ type: 'success', text: `Sent to ${sent.size} of ${sent.size + remaining.length} on this row. The other ${remaining.length} ${remaining.length === 1 ? 'is' : 'are'} still ticked and ${row.company_name || 'the row'} stays on the list — press Send again for them, or Done to close it.` });
    }
    return { closed: false, remaining: remaining.length };
  };

  /**
   * The connection dropped. Did the mail actually go?
   *
   * A send is not a request that either happens or does not: the server keeps
   * going after the browser stops listening, so a timeout tells us NOTHING
   * about what the recipients received. Treating it as a failure is what got
   * people re-pressing Send and mailing a prospect twice.
   *
   * So ask the record of what actually went out. Every send writes a row per
   * recipient to the email log, and this reads it back for this lead.
   */
  const confirmAfterTimeout = async (row, recipients) => {
    const wanted = recipients.map(e => String(e || '').trim().toLowerCase()).filter(Boolean);
    try {
      const params = row.lead_id
        ? { leadId: row.lead_id, limit: 100 }
        : { q: row.company_name || '', limit: 100 };
      const res = await dashboardAPI.getEmailLog(params);
      const rows = (res && (res.data || res)) || [];
      const recent = Date.now() - 20 * 60 * 1000;   // this attempt, not last week's
      const delivered = new Set(rows
        .filter(r => String(r.status || '').toLowerCase() === 'sent'
          && new Date(r.sent_at).getTime() >= recent)
        .map(r => String(r.recipient_email || '').trim().toLowerCase()));
      return { delivered: wanted.filter(e => delivered.has(e)), missing: wanted.filter(e => !delivered.has(e)) };
    } catch (err) {
      // The log itself is unreachable — say so rather than guess either way.
      return null;
    }
  };

  // A sent follow-up is finished: mark it complete in the DB and take it off the
  // list. Failures here are non-fatal — the mail already went out, so the row is
  // removed either way rather than sitting there tempting a second send.
  const completeAndRemoveRow = async (row) => {
    if (!row) return;
    try {
      // `!= null`, not a truthiness test: the pitch is follow-up number ZERO,
      // and `if (0)` quietly skipped recording it as sent.
      if (row.lead_id && row.follow_up_number != null) {
        if (row.is_post_sales) {
          await dashboardAPI.markPostSalesFollowUpCompleted(row.lead_id, row.follow_up_number);
        } else {
          // Pass the date this row is FOR. A row queued from the LinkedIn date
          // has no follow_ups record yet, so the server creates one — and
          // without the date it would be filed under today rather than the day
          // the pitch actually went out.
          await leadsAPI.markCompleted(row.lead_id, row.follow_up_number,
            row.follow_up_date ? { followUpDate: row.follow_up_date } : {});
        }
        if (onMarkCompleted) onMarkCompleted(row.lead_id, row.follow_up_number, row.is_post_sales);
      }
    } catch (err) {
      console.warn('Could not mark follow-up complete after send:', err?.message);
    } finally {
      setRows(prev => prev.filter(r => r.id !== row.id));
    }
  };

  const markDone = async (rowIndex) => {
    const row = rowIndex >= 0 ? rows[rowIndex] : null;
    if (!row) return;
    if (!row.lead_id || row.follow_up_number == null) {
      // If no lead_id, just remove the row locally
      setRows(prev => prev.filter((_, i) => i !== rowIndex));
      setMessage({ type: 'success', text: 'Row removed.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    setMarkingDoneId(row.id);
    setMessage({ type: '', text: '' });
    try {
      // Mark the follow-up as completed in the database
      if (row.is_post_sales) {
        await dashboardAPI.markPostSalesFollowUpCompleted(row.lead_id, row.follow_up_number);
      } else {
        await leadsAPI.markCompleted(row.lead_id, row.follow_up_number,
          row.follow_up_date ? { followUpDate: row.follow_up_date } : {});
      }
      // Remove the row from the list
      setRows(prev => prev.filter((_, i) => i !== rowIndex));
      // Notify parent to refresh follow-ups list
      if (onMarkCompleted) onMarkCompleted(row.lead_id, row.follow_up_number, row.is_post_sales);
      if (onRefresh) onRefresh();
      setMessage({ type: 'success', text: `${row.company_name || 'Follow-up'} marked as done.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to mark as done' });
    } finally {
      setMarkingDoneId(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const sendRow = async (rowIndex) => {
    const row = rowIndex >= 0 ? rows[rowIndex] : null;
    if (!row) return;
    const fromEmails = (row.emails || []).map(e => (typeof e === 'string' ? e.trim() : '')).filter(Boolean);
    const fromCc = (row.ccEmails || []).map(e => (typeof e === 'string' ? e.trim() : '')).filter(e => e && e.includes('@'));
    const asTo = row.recipientAsTo || {};
    // To = only main recipients that are checked
    const to = fromEmails.filter(e => asTo[(e || '').toLowerCase()] === true);
    // CC = only CC list recipients that are checked (unchecked = do not send to them)
    const cc = (row.includeCc !== false) ? fromCc.filter(e => asTo[(e || '').toLowerCase()] === true) : [];
    if (to.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one recipient (check the box) to send.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    const companyName = (row.company_name || '').trim() || 'there';
    const subject = (row.subject || DEFAULT_SUBJECT).replace(COMPANY_PLACEHOLDER, companyName);
    const toWithFirst = to.map(email => recipientFor(row, email));
    // A body with a pasted signature in it can be megabytes of base64, and the
    // request has a hard 4.5 MB ceiling — settle the pictures before the send
    // rather than letting it come back as "Request Entity Too Large".
    const html = await slimEmailBody(buildEmailHtml(row.template || DEFAULT_TEMPLATE, globalEmailStyle));
    setSendingId(row.id);
    setMessage({ type: '', text: '' });
    try {
      let attachments;
      if ((row.attachments || []).length > 0) {
        const results = await Promise.allSettled((row.attachments || []).map(readFileAsBase64));
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length) console.warn('Attachment read failed:', failed.map(f => f.reason?.message));
        attachments = results.filter(r => r.status === 'fulfilled' && r.value && String(r.value.content || '').length > 0).map(r => r.value);
        if (attachments.length === 0 && (row.attachments || []).length > 0) {
          setMessage({ type: 'error', text: 'Could not read attachment(s). Try again or use a smaller file.' });
          setTimeout(() => setMessage({ type: '', text: '' }), 4000);
          setSendingId(null);
          return;
        }
        if (attachments.length === 0) attachments = undefined;
      } else {
        attachments = undefined;
      }
      const effectiveSlot = (user?.email_sender_slot >= 1 && user?.email_sender_slot <= 4) ? user.email_sender_slot : (senderSlot >= 1 && senderSlot <= 4 ? senderSlot : undefined);
      const result = await dashboardAPI.sendFollowUpEmails({
        to: toWithFirst,
        cc: cc.length > 0 ? cc : undefined,
        subject,
        html,
        companyName,
        stage: row.stage || undefined,
        senderSlot: effectiveSlot,
        userId: user?.id,
        userEmail: user?.email,
        attachments,
        leadId: row.lead_id || undefined,
        followUpNumber: row.follow_up_number != null ? row.follow_up_number : undefined
      });
      if (result.success) {
        const settled = await settleRowAfterSend(row, to, true);
        if (settled.closed) {
          setMessage({ type: 'success', text: `Sent to ${result.sent} recipient(s) — ${row.company_name || 'this follow-up'} removed from the list.` });
        }
      } else {
        setMessage({ type: 'error', text: result.error || (result.failed ? `${result.failed} failed` : 'Send failed') });
      }
    } catch (err) {
      // No answer is not the same as no send. The server runs for up to five
      // minutes; if the browser gave up, or the connection dropped, the mail may
      // well be on its way — so check the record before saying anything.
      const noAnswer = err.code === 'ECONNABORTED' || !err.response || /timeout|network/i.test(err.message || '');
      if (noAnswer) {
        const outcome = await confirmAfterTimeout(row, to);
        if (outcome && outcome.missing.length === 0 && outcome.delivered.length > 0) {
          const settled = await settleRowAfterSend(row, outcome.delivered, true);
          if (settled.closed) {
            setMessage({ type: 'success', text: `Sent to ${outcome.delivered.length} recipient(s) — the reply was slow to come back, but the send log confirms every one went out. ${row.company_name || 'This follow-up'} removed from the list.` });
          }
          return;
        }
        if (outcome && outcome.delivered.length > 0) {
          // Untick the ones that already went, so pressing Send again cannot
          // mail the same person twice.
          setRows(prev => prev.map(r => (r.id === row.id
            ? { ...r, recipientAsTo: { ...(r.recipientAsTo || {}),
                ...Object.fromEntries(outcome.delivered.map(e => [e, false])) } }
            : r)));
          setMessage({ type: 'error', text: `Part sent: ${outcome.delivered.length} went out, ${outcome.missing.length} did not (${outcome.missing.join(', ')}). The ones already sent have been unticked — press Send again for the rest.` });
          return;
        }
        setMessage({ type: 'error', text: outcome
          ? 'No reply from the server and nothing recorded as sent. Check the Email History before trying again — do not assume it failed.'
          : 'No reply from the server, and the send log could not be reached. Check Email History before trying again.' });
        return;
      }
      const errMsg = err.response?.data?.error || err.message || 'Failed to send';
      const hint = (errMsg && (errMsg.includes('Email configuration') || errMsg.includes('EAUTH') || errMsg.includes('authentication')))
        ? ' Check backend EMAIL_USER and EMAIL_PASSWORD (or EMAIL_APP_PASSWORD) in .env.'
        : '';
      setMessage({ type: 'error', text: errMsg + hint });
    } finally {
      setSendingId(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
    }
  };

  // Hand this row to Outlook: same recipients, subject, body and attachments,
  // opened as a draft in the person's own mailbox so they can finish it there.
  const openInOutlook = async (rowIndex) => {
    const row = rowIndex >= 0 ? rows[rowIndex] : null;
    if (!row) return;
    const fromEmails = (row.emails || []).map(e => (typeof e === 'string' ? e.trim() : '')).filter(Boolean);
    const fromCc = (row.ccEmails || []).map(e => (typeof e === 'string' ? e.trim() : '')).filter(e => e && e.includes('@'));
    const asTo = row.recipientAsTo || {};
    const to = fromEmails.filter(e => asTo[(e || '').toLowerCase()] === true);
    const cc = (row.includeCc !== false) ? fromCc.filter(e => asTo[(e || '').toLowerCase()] === true) : [];
    if (to.length === 0) {
      setMessage({ type: 'error', text: 'Tick at least one recipient first.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    setDraftingId(row.id);
    setMessage({ type: '', text: '' });
    try {
      let attachments;
      if ((row.attachments || []).length > 0) {
        const results = await Promise.allSettled((row.attachments || []).map(readFileAsBase64));
        attachments = results.filter(r => r.status === 'fulfilled' && r.value).map(r => r.value);
        if (attachments.length === 0) attachments = undefined;
      }
      const res = await dashboardAPI.createOutlookDraft({
        to,
        cc: cc.length ? cc : undefined,
        subject: (row.subject || '').replace(/{{company_name}}/g, row.company_name || ''),
        html: await slimEmailBody(buildEmailHtml(row.template || DEFAULT_TEMPLATE, globalEmailStyle)),
        attachments,
        userId: user?.id,
        userEmail: user?.email
      });
      if (res?.success && (res.composeUrl || res.webLink)) {
        window.open(res.composeUrl || res.webLink, '_blank', 'noopener');
        setMessage({ type: 'success', text: 'Draft created in your Outlook — finish and send it there.' });
      } else {
        setMessage({ type: 'error', text: res?.error || 'Could not create the Outlook draft.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Could not create the Outlook draft.' });
    } finally {
      setDraftingId(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const sendAll = async () => {
    // Only send filtered rows (the ones currently visible)
    const rowsToSend = filteredRows.filter(r => (r.emails || []).some(e => (e || '').trim()));
    if (rowsToSend.length === 0) {
      setMessage({ type: 'error', text: 'No rows with emails to send.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    setSendingAll(true);
    setMessage({ type: '', text: '' });
    let sent = 0;
    let failed = 0;
    // Rows that went out but still have addresses waiting — they stay on screen.
    let partial = 0;
    for (const row of rowsToSend) {
      const fromEmails = (row.emails || []).map(e => (e || '').trim()).filter(Boolean);
      const fromCc = (row.ccEmails || []).map(e => (e || '').trim()).filter(e => e && e.includes('@'));
      const asTo = row.recipientAsTo || {};
      const to = fromEmails.filter(e => asTo[(e || '').toLowerCase()] === true);
      const cc = (row.includeCc !== false) ? fromCc.filter(e => asTo[(e || '').toLowerCase()] === true) : [];
      if (to.length === 0) continue;
      try {
        let attachments;
        if ((row.attachments || []).length > 0) {
          const results = await Promise.allSettled((row.attachments || []).map(readFileAsBase64));
          attachments = results.filter(r => r.status === 'fulfilled' && r.value?.content).map(r => r.value);
          if (attachments.length === 0) attachments = undefined;
        } else {
          attachments = undefined;
        }
        const effectiveSlot = (user?.email_sender_slot >= 1 && user?.email_sender_slot <= 4) ? user.email_sender_slot : (senderSlot >= 1 && senderSlot <= 4 ? senderSlot : undefined);
        const result = await dashboardAPI.sendFollowUpEmails({
          to: to.map(email => recipientFor(row, email)),
          cc: cc.length > 0 ? cc : undefined,
            subject: (row.subject || DEFAULT_SUBJECT).replace(COMPANY_PLACEHOLDER, row.company_name || 'there'),
          html: await slimEmailBody(buildEmailHtml(row.template || DEFAULT_TEMPLATE, globalEmailStyle)),
          companyName: row.company_name || 'there',
          stage: row.stage || undefined,
          senderSlot: effectiveSlot,
          userId: user?.id,
          userEmail: user?.email,
          attachments,
          // Same threading key as the single-row send above — "Send all" must
          // continue existing conversations, not start new ones.
          leadId: row.lead_id || undefined,
          followUpNumber: row.follow_up_number != null ? row.follow_up_number : undefined
        });
        if (result.success) {
          sent += result.sent || 0;
          // Rows with addresses still unsent stay on the list; see settleRowAfterSend.
          const settled = await settleRowAfterSend(row, to, false);
          if (!settled.closed) partial += 1;
        } else failed += 1;
      } catch (e) {
        // Same rule as the single send: no answer is not proof of no send.
        const noAnswer = e.code === 'ECONNABORTED' || !e.response || /timeout|network/i.test(e.message || '');
        if (noAnswer) {
          const outcome = await confirmAfterTimeout(row, to);
          if (outcome && outcome.missing.length === 0 && outcome.delivered.length > 0) {
            sent += outcome.delivered.length;
            const settled = await settleRowAfterSend(row, outcome.delivered, false);
            if (!settled.closed) partial += 1;
            continue;
          }
          if (outcome && outcome.delivered.length > 0) {
            setRows(prev => prev.map(r => (r.id === row.id
              ? { ...r, recipientAsTo: { ...(r.recipientAsTo || {}),
                  ...Object.fromEntries(outcome.delivered.map(addr => [addr, false])) } }
              : r)));
            sent += outcome.delivered.length;
          }
        }
        failed += 1;
        const errMsg = e.response?.data?.error || e.message;
        if (failed === 1 && errMsg && (errMsg.includes('Email configuration') || errMsg.includes('EAUTH'))) {
          setMessage({ type: 'error', text: `Send failed: ${errMsg}. Set EMAIL_USER and EMAIL_PASSWORD in backend .env.` });
          setSendingAll(false);
          setTimeout(() => setMessage({ type: '', text: '' }), 6000);
          return;
        }
      }
    }
    setMessage({ type: failed ? 'error' : 'success', text: `Sent ${sent} email(s).`
      + `${partial ? ` ${partial} row(s) still have addresses nobody has written to yet, so they stayed on the list.` : ''}`
      + `${failed ? ` ${failed} row(s) failed and stayed.` : ''}` });
    setSendingAll(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="w-8 h-8 text-[#679632]" />
          Send Emails
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {(() => {
            // When the backend can send as this person's own mailbox, show THAT
            // address — showing the shared Gmail slot instead was simply wrong.
            if (effectiveSender?.from) {
              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Sending from:</span>
                  <span className="text-sm font-medium text-gray-800">{effectiveSender.from}</span>
                  {effectiveSender.ownMailbox && (
                    <span
                      title="Sent through Microsoft 365 from your own mailbox, so replies come back to you."
                      className="text-[11px] px-1.5 py-0.5 rounded bg-[#eaf3e0] text-[#4a6f22] font-medium"
                    >
                      your mailbox
                    </span>
                  )}
                </div>
              );
            }
            const userHasAssignedSlot = user?.email_sender_slot >= 1 && user?.email_sender_slot <= 4;
            if (userHasAssignedSlot) {
              const assigned = emailSenders.find(s => s.slot === user.email_sender_slot);
              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Sending from:</span>
                  <span className="text-sm font-medium text-gray-800">{assigned?.email || `Account ${user.email_sender_slot}`}</span>
                </div>
              );
            }
            if (emailSenders.length > 1) {
              return (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Send from:</label>
                  <select
                    value={senderSlot ?? ''}
                    onChange={(e) => setSenderSlot(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                  >
                    {emailSenders.map((s) => (
                      <option key={s.slot} value={s.slot}>
                        {s.email || `Account ${s.slot}`}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            return null;
          })()}
          <button
            type="button"
            onClick={scrapeAll}
            disabled={loading || scraping || filteredRows.length === 0}
            title="Pull recipient emails from each lead's contact details (POC emails + suggested firstname@company.com). Fills the To field for rows that don't have emails yet."
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            {scraping ? 'Scraping...' : 'Scrape All Emails'}
          </button>
          <button
            type="button"
            onClick={addNewRow}
            className="px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] flex items-center gap-2"
          >
            <Plus size={18} />
            Add company / row
          </button>
          <button
            type="button"
            onClick={sendAll}
            disabled={sendingAll || filteredRows.every(r => !(r.emails || []).filter(Boolean).length)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Send size={18} />
            {sendingAll ? 'Sending...' : 'Send all'}
          </button>
          <div className="ml-auto">
            <PageOptionsButton onExportExcel={onExportExcel} onExportPDF={onExportPDF} showExport={showExport} />
          </div>
        </div>
      </div>

      {/* Filter Tabs - counts update when you click Refresh */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
        <span className="text-sm font-medium text-gray-600 mr-2">Show:</span>
        <button
          type="button"
          onClick={() => setFilter('today')}
          title="Follow-ups due today"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            effectiveFilter === 'today'
              ? 'bg-red-100 text-red-800 border-2 border-red-300'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
          }`}
        >
          Today
          <span className={`px-2 py-0.5 rounded-full text-xs ${effectiveFilter === 'today' ? 'bg-red-200' : 'bg-gray-200'}`}>
            {counts.todayCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setFilter('week')}
          title="Follow-ups due anytime this week (Mon–Sun)"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            effectiveFilter === 'week'
              ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
          }`}
        >
          This Week
          <span className={`px-2 py-0.5 rounded-full text-xs ${effectiveFilter === 'week' ? 'bg-blue-200' : 'bg-gray-200'}`}>
            {counts.weekCount}
          </span>
        </button>
        {/* "All Pending" is admin-only — BDEs work from Today / This Week */}
        {!restrictToOwn && (
        <button
          type="button"
          onClick={() => setFilter('all')}
          title="All follow-ups not yet marked Done"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            filter === 'all'
              ? 'bg-[#679632] text-white border-2 border-[#587f2b]'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
          }`}
        >
          All Pending
          <span className={`px-2 py-0.5 rounded-full text-xs ${filter === 'all' ? 'bg-[#587f2b]' : 'bg-gray-200'}`}>
            {counts.allCount}
          </span>
        </button>
        )}
        {restrictToOwn && (
          <span className="text-xs text-gray-500 ml-1">Showing your follow-ups only</span>
        )}
        </div>

      {message.text && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {message.text}
        </div>
      )}

      {/* Fixed toolbar: style + format in one row, stays visible when scrolling */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 py-3 px-4 -mx-4 mb-4 bg-white border-b border-gray-200 shadow-sm">
        <span className="text-xs font-medium text-gray-600 shrink-0">Style:</span>
        <select
          value={globalEmailStyle.fontFamily || DEFAULT_EMAIL_STYLE.fontFamily}
          onChange={(e) => setGlobalEmailStyle(s => ({ ...s, fontFamily: e.target.value }))}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
          title="Font"
        >
          {EMAIL_FONT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={globalEmailStyle.fontSize || DEFAULT_EMAIL_STYLE.fontSize}
          onChange={(e) => setGlobalEmailStyle(s => ({ ...s, fontSize: e.target.value }))}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
          title="Size"
        >
          {EMAIL_FONT_SIZE_OPTIONS.map((px) => (
            <option key={px} value={px}>{px}</option>
          ))}
        </select>
        <select
          value={globalEmailStyle.lineHeight || DEFAULT_EMAIL_STYLE.lineHeight}
          onChange={(e) => setGlobalEmailStyle(s => ({ ...s, lineHeight: e.target.value }))}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
          title="Line height"
        >
          {EMAIL_LINE_HEIGHT_OPTIONS.map((lh) => (
            <option key={lh} value={lh}>{lh}</option>
          ))}
        </select>
        <span className="text-gray-300 mx-1">|</span>
        <span className="text-xs font-medium text-gray-600 shrink-0">Align:</span>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Align left" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyLeft', false, null); }}>L</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Align center" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyCenter', false, null); }}>C</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Align right" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyRight', false, null); }}>R</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Justify" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyFull', false, null); }}>J</button>
        <span className="text-gray-300 mx-1">|</span>
        <span className="text-xs font-medium text-gray-600 shrink-0">Format:</span>
        <button type="button" className="px-2 py-1.5 text-sm font-bold border border-gray-300 bg-white rounded hover:bg-gray-50" title="Bold" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('bold', false, null); }}>B</button>
        <button type="button" className="px-2 py-1.5 text-sm italic border border-gray-300 bg-white rounded hover:bg-gray-50" title="Italic" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('italic', false, null); }}>I</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 underline" title="Underline" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('underline', false, null); }}>U</button>
        {/* Bullets. execCommand builds the <ul><li> the editor shows; the send
            path then spells the marker out inline, because a mail client will
            not apply our stylesheet (see inlineListStylesIn). */}
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Bullet list — turns the line (or the selected lines) into bullet points, and off again" onMouseDown={(e) => { e.preventDefault(); runInActiveEditor('insertUnorderedList'); }}>• List</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Numbered list" onMouseDown={(e) => { e.preventDefault(); runInActiveEditor('insertOrderedList'); }}>1. List</button>
        {/* Text colour. The swatch is a real colour input, so the whole palette
            is available; the presets beside it are the common ones in one click. */}
        <span className="inline-flex items-center gap-1 pl-2 ml-1 border-l border-gray-300">
          <span className="text-xs font-medium text-gray-600">Colour:</span>
          {[['#000000', 'Black'], ['#679632', 'AGS green'], ['#c0392b', 'Red'], ['#2563eb', 'Blue'], ['#6b7280', 'Grey']].map(([hex, label]) => (
            <button
              key={hex}
              type="button"
              title={`${label} — select text first`}
              onMouseDown={(e) => { e.preventDefault(); runInActiveEditor('foreColor', hex); }}
              className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform"
              style={{ backgroundColor: hex }}
            />
          ))}
          <input
            type="color"
            title="Any other colour — select the text first, then pick"
            defaultValue="#679632"
            onMouseDown={rememberSelection}
            onChange={(e) => runInActiveEditor('foreColor', e.target.value, { restore: true })}
            className="w-7 h-7 p-0 border border-gray-300 rounded bg-white cursor-pointer"
          />
        </span>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 text-blue-600" title="Insert link (select text first)" onMouseDown={(e) => { e.preventDefault(); const sel = window.getSelection(); const range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null; if (range && activeEditorRef.current && activeEditorRef.current.contains(range.commonAncestorContainer)) savedSelectionRef.current = range; const url = window.prompt('Enter URL:'); const el = activeEditorRef.current; if (url && el) { el.focus(); if (savedSelectionRef.current) { try { const s = window.getSelection(); s.removeAllRanges(); s.addRange(savedSelectionRef.current); } catch (_) {} savedSelectionRef.current = null; } document.execCommand('createLink', false, url.trim().startsWith('http') ? url.trim() : 'https://' + url.trim()); } }}>Link</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Insert image into the email body" onMouseDown={(e) => { e.preventDefault(); inlineImageInputRef.current?.click(); }}>Image</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Insert a table into the email body" onMouseDown={(e) => { e.preventDefault(); const el = activeEditorRef.current; if (!el) return; const spec = window.prompt('Table size as rows x columns:', '3x3'); if (!spec) return; const [r, c] = spec.toLowerCase().split(/[x×,\s]+/).map(n => parseInt(n, 10)); if (!r || !c) return; insertHtmlAtCaret(el, buildTableHtml(Math.min(r, 20), Math.min(c, 10))); const idx = activeRowIndexRef.current; if (idx != null && idx >= 0) updateRow(idx, { template: el.innerHTML }); }}>Table</button>
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 text-gray-600" title="Tidy up Word/Outlook paste — removes stray boxes, page widths and blank spacer lines, keeps your formatting" onMouseDown={(e) => { e.preventDefault(); const el = activeEditorRef.current; if (!el) return; el.innerHTML = sanitizePastedHtml(el.innerHTML); const idx = activeRowIndexRef.current; if (idx != null && idx >= 0) updateRow(idx, { template: el.innerHTML }); }}>Tidy</button>
        <input ref={inlineImageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; const el = activeEditorRef.current; if (!file || !el) return; const reader = new FileReader(); reader.onload = (ev) => { insertHtmlAtCaret(el, `<img src="${ev.target.result}" style="max-width:100%;height:auto;display:block;" />`); const idx = activeRowIndexRef.current; if (idx != null && idx >= 0) updateRow(idx, { template: el.innerHTML }); }; reader.readAsDataURL(file); }} />
        {selectedImg && (
          <span className="flex items-center gap-1 pl-2 ml-1 border-l border-gray-300">
            <span className="text-xs text-gray-600">
              {pickedImgs.length >= 2 ? `${pickedImgs.length} picked` : 'Image'}
            </span>
            <input
              type="number" min="8" max="2000" value={selectedImgWidth}
              onChange={(e) => setSelectedImgWidth(e.target.value)}
              onBlur={() => resizeSelected(selectedImgWidth)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resizeSelected(selectedImgWidth); } }}
              className="w-16 border border-gray-300 rounded px-1 py-0.5 text-xs"
              title="Width in pixels — height follows"
            />
            <span className="text-xs text-gray-500">px</span>
            <button type="button" title={selectedImgHref ? `Linked to ${selectedImgHref} — click to change or remove` : 'Link this image to a URL (LinkedIn, Instagram, your site)'} onMouseDown={(e) => { e.preventDefault(); linkSelectedImage(); }} className={`px-2 py-1.5 text-xs rounded font-medium border ${selectedImgHref ? 'border-[#679632] text-[#679632] bg-[#f1f7ec]' : 'border-blue-300 text-blue-600 bg-white hover:bg-blue-50'}`}>{selectedImgHref ? '🔗 Linked' : '🔗 Link'}</button>
            <button type="button" title="Move this image one place left" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('moveLeft'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">◀</button>
            <button type="button" title="Move this image one place right" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('moveRight'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">▶</button>
            <button type="button" title="Line this image up with the image-only lines below it, as one row" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('row'); }} className="px-2 py-1.5 text-xs border border-[#679632] text-[#679632] bg-white rounded hover:bg-[#f1f7ec] font-medium">Make a row</button>
            <button type="button" title="Put this image on the line above, beside the previous one" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('join'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">Same line</button>
            <button type="button" title="Give this image its own line" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('own'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">Own line</button>
            <button type="button" title="Align this line left" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('left'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">⇤</button>
            <button type="button" title="Centre this line" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('center'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">⇔</button>
            <button type="button" title="Align this line right" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('right'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">⇥</button>
            <button type="button" title="Deselect" onMouseDown={(e) => { e.preventDefault(); selectComposerImage(null); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50 text-gray-500">✕</button>
          </span>
        )}
        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Attach files" onMouseDown={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}>Attach</button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => { const files = e.target.files ? Array.from(e.target.files) : []; if (!files.length) { e.target.value = ''; return; } let idx = activeRowIndexRef.current; if (idx == null && filteredRows.length > 0) idx = rows.findIndex(r => r.id === filteredRows[0].id); if (idx != null && idx >= 0) { setRows(prev => { const next = [...prev]; if (idx >= 0 && idx < next.length) next[idx] = { ...next[idx], attachments: [...(next[idx].attachments || []), ...files] }; return next; }); } e.target.value = ''; }} />
      </div>

      <p className="text-xs text-gray-500">
        CC: {SALES_GROUP_EMAIL} on the initial pitch, {FOLLOWUP_GROUP_EMAIL} on follow-ups —
        added to every row automatically, and visible to the recipient. Swap one for the other in
        the CC box if a row needs it; the server makes sure one of them is always copied.
      </p>

      {(loading && rows.length === 0) ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#679632]" />
        </div>
      ) : (
        <div className="space-y-6">
          {filteredRows.map((row) => {
            // Find the actual index in the rows array for updates
            const rowIndex = rows.findIndex(r => r.id === row.id);
            return (
            <div key={row.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={row.company_name || ''}
                    onChange={(e) => updateRow(rowIndex, { company_name: e.target.value })}
                    placeholder="Company name"
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-48 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                  />
                  {/* Which step of the pitch this row is. It is NOT a second
                      template picker: there is one of those, below, and it
                      lists your own saved templates as well as the stages.
                      Two controls that set the same thing — one of which could
                      not see your templates — was the confusing part. */}
                  <span
                    className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium"
                    title="The step of the pitch this follow-up is recorded against. Pick a different template below to change it."
                  >
                    {row.stage || 'Initial Sales Pitch'}
                  </span>
                  {row.follow_up_date && <span className="text-xs text-gray-500">({row.follow_up_date})</span>}
                  {row.isInitialPitch && (
                    <span
                      className="px-2 py-0.5 rounded-full bg-green-50 text-green-800 text-[11px] font-medium border border-green-200"
                      title="The lead's initial sales pitch, on the First Sales Message Sent date. Not a follow-up — sending it (or Done) records the pitch as sent and leaves the follow-ups alone."
                    >
                      initial pitch
                    </span>
                  )}
                  {/* Why a row you have already sent is still here: somebody on
                      it has not had it yet. Without this the row looked exactly
                      like a send that had failed. */}
                  {(() => {
                    const already = new Set(row.alreadyEmailed || []);
                    const addresses = (row.emails || []).map(e => String(e || '').trim().toLowerCase()).filter(e => e.includes('@'));
                    const done = addresses.filter(e => already.has(e)).length;
                    if (!done || !addresses.length) return null;
                    const left = addresses.length - done;
                    return (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${left ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}
                        title={left
                          ? `${done} of these ${addresses.length} addresses already had this email, so they are unticked. The row stays until the remaining ${left} have had it — or press Done to close it.`
                          : 'Everyone on this row has already had it.'}
                      >
                        {done} of {addresses.length} sent{left ? ` · ${left} left` : ''}
                      </span>
                    );
                  })()}
                  {(row.notPitched || []).length > 0 && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-medium border bg-amber-50 text-amber-800 border-amber-200"
                      title={`These contacts never received the initial pitch, so they are unticked on this follow-up: ${(row.notPitched || []).join(', ')}. Send them the pitch first, or tick them here on purpose.`}
                    >
                      {row.notPitched.length} never had the pitch · unticked
                    </span>
                  )}
                  {row.lead_id && onViewLead && (
                    <button
                      type="button"
                      onClick={() => onViewLead(row.lead_id)}
                      title="View this company's pre-sales form"
                      className="p-2 text-gray-600 hover:text-[#679632] hover:bg-gray-100 rounded-lg"
                    >
                      <ExternalLink size={18} />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => sendRow(rowIndex)}
                    disabled={sendingId !== null || markingDoneId !== null || ![...(row.emails || []).map(e => (e || '').trim()).filter(Boolean), ...(row.ccEmails || []).map(e => (e || '').trim()).filter(e => e && e.includes('@'))].some(e => (row.recipientAsTo || {})[(e || '').toLowerCase()] === true)}
                    className="px-3 py-1.5 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 text-sm flex items-center gap-1"
                  >
                    <Send size={14} />
                    {sendingId === row.id ? 'Sending...' : 'Send'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openInOutlook(rowIndex)}
                    disabled={draftingId !== null || sendingId !== null}
                    title="Create this email as a draft in your own Outlook and open it there — paste, attach and send from Outlook"
                    className="px-3 py-1.5 border border-[#0f6cbd] text-[#0f6cbd] rounded-lg hover:bg-[#eaf2fb] disabled:opacity-50 text-sm flex items-center gap-1"
                  >
                    <ExternalLink size={14} />
                    {draftingId === row.id ? 'Opening…' : 'Open in Outlook'}
                  </button>
                  <button
                    type="button"
                    onClick={() => markDone(rowIndex)}
                    disabled={sendingId !== null || markingDoneId !== null}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-1"
                    title="Mark as done and remove from list"
                  >
                    <CheckCircle size={14} />
                    {markingDoneId === row.id ? 'Marking...' : 'Done'}
                  </button>
                  <button type="button" onClick={() => deleteRow(rowIndex)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete this row for good — the follow-up record goes too, so it will not come back on the next refresh. The follow-up date stays on the lead's Pre-Sales form.">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              {/* One column: the body gets the full card width, so a signature or
                  table lays out here exactly as it does on the Templates page.
                  In two columns it was squeezed into half the width and wrapped. */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-0.5">To</label>
                    <div className="space-y-1">
                      {/* Only the people who still need this email.
                          A lead's contact book can hold twenty addresses; once
                          nineteen have had the pitch, showing all twenty with
                          nineteen unticked is noise you have to read to
                          understand. The ones already written to are folded
                          away behind a line that says how many — the INDEX of
                          each row is kept, so editing still edits the right
                          address. */}
                      {(() => {
                        const all = (row.emails || ['']).map((email, ei) => ({ email, ei }));
                        const already = new Set(row.alreadyEmailed || []);
                        const outstanding = all.filter(({ email }) => !already.has(String(email || '').trim().toLowerCase()));
                        const hidden = all.length - outstanding.length;
                        const showEverything = expandedRecipients[row.id] || hidden === 0 || outstanding.length === 0;
                        return (showEverything ? all : outstanding).map(({ email, ei }) => (
                        <div key={ei} className="flex gap-2 items-center">
                          <label className="flex items-center gap-1.5 shrink-0 cursor-pointer" title={((row.recipientAsTo || {})[(email || '').trim().toLowerCase()] !== false) ? 'Send as To' : 'Send as CC'}>
                            <input
                              type="checkbox"
                              checked={((row.recipientAsTo || {})[(email || '').trim().toLowerCase()] !== false)}
                              onChange={() => toggleRecipientToCc(rowIndex, ei)}
                              className="rounded border-gray-300 text-[#679632] focus:ring-[#679632]"
                            />
                          </label>
                          <input
                            type="email"
                            value={email || ''}
                            onChange={(e) => updateEmail(rowIndex, ei, e.target.value)}
                            placeholder="email@company.com"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          />
                          {/* The name this person is greeted by. Pre-filled from
                              the contact record, or worked out from the address,
                              and editable — so "Hello there" is visible and
                              fixable BEFORE the email goes, not after. */}
                          <input
                            type="text"
                            value={firstNameFor(row, email)}
                            onChange={(e) => updateEmail(rowIndex, ei, email, e.target.value)}
                            placeholder="First name"
                            title={isGuessedFirstName(row, email)
                              ? 'Guessed from the address — the send checks the lead\'s contacts and our staff list first, but type over it to be sure'
                              : 'Fills {{first_name}} in the greeting'}
                            className={`w-32 shrink-0 border rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent ${
                              isGuessedFirstName(row, email)
                                ? 'border-amber-400 bg-amber-50 text-amber-900'
                                : 'border-gray-300'}`}
                          />
                        </div>
                        ));
                      })()}
                      {(() => {
                        const all = (row.emails || []).map(e => String(e || '').trim().toLowerCase()).filter(Boolean);
                        const already = new Set(row.alreadyEmailed || []);
                        const hidden = all.filter(e => already.has(e)).length;
                        if (!hidden || hidden === all.length) return null;
                        const shown = expandedRecipients[row.id];
                        return (
                          <button
                            type="button"
                            onClick={() => setExpandedRecipients(prev => ({ ...prev, [row.id]: !prev[row.id] }))}
                            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            {shown ? `Hide the ${hidden} already written to` : `${hidden} already had this email — show`}
                          </button>
                        );
                      })()}
                      <button type="button" onClick={() => addEmailToRow(rowIndex)} className="text-sm text-[#679632] hover:underline flex items-center gap-1">
                        <Plus size={14} /> Add recipient
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <label className="text-sm font-medium text-gray-700">CC</label>
                      <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-600" title="Uncheck to send without CC">
                        <input
                          type="checkbox"
                          checked={row.includeCc !== false}
                          onChange={() => updateRow(rowIndex, { includeCc: row.includeCc === false })}
                          className="rounded border-gray-300 text-[#679632] focus:ring-[#679632]"
                        />
                        Include CC
                      </label>
                    </div>
                    <div className="space-y-1">
                      {(row.ccEmails || []).length === 0 ? (
                        <button type="button" onClick={() => addCcEmailToRow(rowIndex)} className="text-sm text-gray-500 hover:text-[#679632] flex items-center gap-1">
                          <Plus size={14} /> Add CC
                        </button>
                      ) : (
                        <>
                          {row.ccEmails.map((ccEmail, ci) => {
                            const emailTrim = (ccEmail || '').trim().toLowerCase();
                            const includeAsCc = emailTrim && (row.recipientAsTo || {})[emailTrim] === true;
                            return (
                              <div key={ci} className="flex gap-2 items-center">
                                <label className="flex items-center gap-1.5 shrink-0 cursor-pointer" title={includeAsCc ? 'Included as CC' : 'Excluded (uncheck = not sent)'}>
                                  <input
                                    type="checkbox"
                                    checked={!!(emailTrim && includeAsCc)}
                                    onChange={() => toggleRecipientAsToByEmail(rowIndex, ccEmail)}
                                    className="rounded border-gray-300 text-[#679632] focus:ring-[#679632]"
                                  />
                                </label>
                                <input
                                  type="email"
                                  value={ccEmail || ''}
                                  onChange={(e) => updateCcEmail(rowIndex, ci, e.target.value)}
                                  placeholder="cc@company.com"
                                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent bg-gray-50"
                                />
                              </div>
                            );
                          })}
                          <button type="button" onClick={() => addCcEmailToRow(rowIndex)} className="text-sm text-[#679632] hover:underline flex items-center gap-1">
                            <Plus size={14} /> Add CC
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template
                      {row.templateLoading && <span className="ml-2 text-xs font-normal text-gray-500">loading…</span>}
                    </label>
                    <select
                      value={row.templateKey || `stage:${row.stage || 'Initial Sales Pitch'}`}
                      onChange={(e) => applyTemplateToRow(rowIndex, e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                      title="Picking a stage also sets the step this follow-up is recorded against."
                    >
                      <optgroup label="Stages">
                        {STAGE_OPTIONS.map(s => (
                          <option key={s} value={`stage:${s}`}>{s}</option>
                        ))}
                      </optgroup>
                      {(customTemplates || []).some(c => c.mine !== false) && (
                        <optgroup label="My templates">
                          {(customTemplates || []).filter(c => c.mine !== false).map(c => (
                            <option key={c.id} value={`custom:${c.id}`}>{c.name}</option>
                          ))}
                        </optgroup>
                      )}
                      {(customTemplates || []).some(c => c.mine === false) && (
                        <optgroup label="Team templates">
                          {(customTemplates || []).filter(c => c.mine === false).map(c => (
                            <option key={c.id} value={`custom:${c.id}`}>
                              {c.name}{c.owner_name ? ` — ${c.owner_name}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={row.subject || ''}
                      onChange={(e) => updateRow(rowIndex, { subject: e.target.value })}
                      placeholder={DEFAULT_SUBJECT}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                    />
                    {/* Exactly what this row will send, with {{company_name}}
                        already filled in from the Company field above. No more
                        finding out what went out by reading the sent folder. */}
                    <div className="mt-1.5 text-xs text-gray-500 break-words">
                      <span className="font-medium text-gray-600">Goes out as:</span>{' '}
                      {(() => {
                        // A row that continues a conversation is sent as a reply
                        // under that conversation's subject — show THAT, not the
                        // stage subject sitting in the box, so nothing about the
                        // outgoing mail is a surprise.
                        const t = threadForRow(row);
                        const typed = (row.subject || DEFAULT_SUBJECT)
                          .replace(COMPANY_PLACEHOLDER, (row.company_name || '').trim() || 'there');
                        if (!t || !t.subject) return <span className="text-gray-800">{typed}</span>;
                        return <span className="text-gray-800">Re: {t.subject}</span>;
                      })()}
                      <span className="ml-2 text-gray-400">
                        → {(row.emails || []).filter(e => (row.recipientAsTo || {})[(e || '').trim().toLowerCase()] === true).length} recipient(s)
                      </span>
                      {(() => {
                        const first = (row.emails || []).find(e => (row.recipientAsTo || {})[(e || '').trim().toLowerCase()] === true);
                        if (!first) return null;
                        const name = firstNameFor(row, first) || 'there';
                        return (
                          <div className="mt-0.5">
                            <span className="font-medium text-gray-600">Greeting:</span>{' '}
                            <span className={name === 'there' ? 'text-amber-700' : 'text-gray-800'}>
                              Hello {name},
                            </span>
                            {name === 'there' && <span className="ml-1 text-amber-700">— add a first name beside the recipient</span>}
                          </div>
                        );
                      })()}
                      {(() => {
                        const t = threadForRow(row);
                        if (!t) return null;
                        const others = (t.recipientCount || 0) - (t.threadedCount || 0);
                        return (
                          <div className="mt-0.5 text-[#4b7327]">
                            🧵 Replies into the conversation already open with{' '}
                            {t.threadedCount} of {t.recipientCount} recipient(s)
                            {t.messageCount > 1 ? ` (${t.messageCount} messages so far)` : ''} —
                            same thread as the pitch.
                            {others > 0 && (
                              <span className="text-gray-500">
                                {' '}The other {others} start a new one.
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                    <DeferredEditor>
                    <TemplateEditor
                      rowIndex={rowIndex}
                      row={row}
                      updateRow={updateRow}
                      plainToHtml={plainToHtml}
                      DEFAULT_TEMPLATE={DEFAULT_TEMPLATE}
                      onFocusEditor={(el, idx) => { activeEditorRef.current = el; activeRowIndexRef.current = idx; }}
                      onSelectImage={(img, additive) => selectComposerImage(img, additive)}
                      emailStyle={globalEmailStyle}
                    />
                    </DeferredEditor>
                    {(row.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-600">Attachments:</span>
                        {(row.attachments || []).map((file, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {file.name || file.filename}
                            <button type="button" onClick={() => updateRow(rowIndex, { attachments: (row.attachments || []).filter((_, j) => j !== i) })} className="text-red-500 hover:text-red-700">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
          })}
          {filteredRows.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {rows.length === 0 
                ? 'No follow-ups with scheduled dates. Add a row manually or schedule follow-ups in Pre/Post Sales.'
                : `No follow-ups for "${filter === 'today' ? 'Today' : filter === 'week' ? 'This Week' : 'All Pending'}". Try a different filter.`
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SendEmails;
